'use client';

import { useEffect, useRef } from 'react';
import { trackUnitPageActive, trackUnitPageInactive } from '@repo/api';
import { UNIT_ENGAGEMENT_CONFIG, createUnitVisitTracker, type UnitVisitTracker } from '@repo/utils';
import { useStore } from '@/providers/StoreProvider';

function createVisitId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Reports unit-page engagement for logged-in users: `page_active` on arrival,
 * again while the user is interacting (at most once per heartbeat window), and
 * exactly one `page_inactive` when the visit ends.
 *
 * Renders nothing. Mounted only on the unit route, so the endpoints can never
 * fire from anywhere else.
 */
export function UnitEngagementTracker({ unitId }: { unitId: string }) {
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  // Both token shapes count as a bearer. Firing before one lands gets a 403,
  // which the response interceptor treats as a dead session — see PromptProvider.
  const authToken = useStore(s => s.webAuth.token);
  const authCustomToken = useStore(s => s.webAuth.customToken);

  const enabled =
    UNIT_ENGAGEMENT_CONFIG.enabled &&
    Boolean(unitId) &&
    sessionHydrated &&
    isLoggedIn &&
    Boolean(authToken || authCustomToken);

  // Read at cleanup time to tell "user left the page" from "user logged out".
  // The latter has no bearer left, so reporting the visit would only earn a 403.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) return;

    const tracker: UnitVisitTracker = createUnitVisitTracker({
      createVisitId,
      onActive: ({ visitId }) => {
        void trackUnitPageActive(unitId, { visitId, surface: 'unit_page' }).catch(() => undefined);
      },
      onInactive: ({ visitId }) => {
        // Always keepalive: a visit can end because the document is tearing
        // down, which cancels an ordinary fetch, and `sendBeacon` can't carry
        // the Authorization header these endpoints authenticate on. It's a
        // no-op for the ends that happen while the page is still alive.
        void trackUnitPageInactive(unitId, { visitId, surface: 'unit_page' }, { keepalive: true }).catch(
          () => undefined,
        );
      },
    });

    // A page opened in a background tab hasn't been seen yet, so hold the visit
    // until it actually becomes visible — otherwise it reports one nobody
    // looked at, which then dies of the idle timeout.
    const activate = () => {
      if (tracker.currentVisitId() === null) tracker.start();
      else tracker.show();
    };
    if (document.visibilityState !== 'hidden') tracker.start();

    const handleInteraction = () => tracker.registerInteraction();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') tracker.hide();
      else activate();
    };
    const handlePageHide = () => tracker.end();
    // Restoring from the bfcache reopens a page whose visit we already closed.
    const handlePageShow = () => activate();

    // Capture phase because scroll doesn't bubble and the unit page scrolls an
    // inner container, not the document.
    const listenerOptions = { capture: true, passive: true } as const;
    document.addEventListener('scroll', handleInteraction, listenerOptions);
    document.addEventListener('pointerdown', handleInteraction, listenerOptions);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('scroll', handleInteraction, listenerOptions);
      document.removeEventListener('pointerdown', handleInteraction, listenerOptions);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);

      // Unmount or a change of unit means the user left this page — including
      // unit A → unit B, which otherwise would only end via the idle timeout.
      if (enabledRef.current) tracker.end();
      tracker.dispose();
    };
  }, [enabled, unitId]);

  return null;
}
